import { StorageBufferAttribute } from 'three/webgpu';

export type GpuOnlyStorageScalarType = 'float32' | 'uint32';
export type GpuOnlyStorageArray = Float32Array | Uint32Array;
export type GpuOnlyStorageArrayConstructor =
  | Float32ArrayConstructor
  | Uint32ArrayConstructor;

/**
 * Only the public backend capabilities needed to materialize and replace a
 * Three.js storage attribute are assumed. Keeping `backend` opaque here makes
 * a normally typed WebGPURenderer assignable even though @types/three does not
 * currently expose these two runtime methods on its base Backend type.
 */
export interface GpuOnlyStorageRenderer {
  readonly backend: unknown;
}

export interface GpuOnlyStorageOptions {
  readonly scalarType: GpuOnlyStorageScalarType;
  readonly count: number;
  readonly itemSize: number;
  readonly label?: string;
  /**
   * Defaults to Three's normal storage-attribute usage set:
   * STORAGE | VERTEX | COPY_SRC | COPY_DST. Custom usage must retain STORAGE
   * and COPY_SRC; add VERTEX, COPY_DST, or INDIRECT when the consumer needs it.
   */
  readonly usage?: GPUBufferUsageFlags;
}

export interface GpuOnlyStorageLayout {
  readonly count: number;
  /** The item size requested by the caller. */
  readonly itemSize: number;
  /**
   * The physical scalar stride. WGSL arrays of vec3 have a four-scalar stride,
   * matching Three's storage-attribute materialization.
   */
  readonly gpuItemSize: number;
  readonly logicalElementCount: number;
  readonly gpuElementCount: number;
  readonly logicalByteLength: number;
  readonly gpuByteLength: number;
  /** At most one physical item (16 bytes for the supported scalar types). */
  readonly cpuPlaceholderByteLength: number;
}

export interface GpuOnlyStorageAllocation<
  TArray extends GpuOnlyStorageArray = GpuOnlyStorageArray,
> extends GpuOnlyStorageLayout {
  readonly attribute: StorageBufferAttribute;
  readonly buffer: GPUBuffer;
  readonly scalarType: GpuOnlyStorageScalarType;
  readonly usage: GPUBufferUsageFlags;
  readonly label: string;
  readonly readbackArrayConstructor: GpuOnlyStorageArrayConstructor;
  readonly destroyed: boolean;
  /**
   * Reconstructs a correctly typed raw readback view. For itemSize=3 the view
   * includes Three/WGSL's fourth padding scalar for every logical item.
   */
  createReadbackView(data: ArrayBuffer): TArray;
  /**
   * Idempotently destroys the injected GPUBuffer. Call only after the
   * attribute is no longer referenced by compute/render bindings.
   */
  destroy(): void;
}

export interface GpuOnlyStorageSelfCheckResult {
  readonly pass: boolean;
  readonly checks: number;
  readonly failures: readonly string[];
}

type StorageBackendRecord = Record<string, unknown> & {
  buffer?: GPUBuffer;
};

type StorageBackend = {
  createStorageAttribute(attribute: StorageBufferAttribute): void;
  get(attribute: StorageBufferAttribute): unknown;
};

type StorageLimitsLike = {
  readonly maxStorageBufferBindingSize: number;
  readonly maxBufferSize: number;
};

const BYTES_PER_SCALAR = 4;
const MIN_ITEM_SIZE = 1;
const MAX_ITEM_SIZE = 4;

function checkedPositiveInteger(value: number, name: string): number {
  if (!Number.isSafeInteger(value) || value < 1) {
    throw new RangeError(`${name} must be a positive safe integer; received ${value}.`);
  }
  return value;
}

function checkedProduct(a: number, b: number, name: string): number {
  const product = a * b;
  if (!Number.isSafeInteger(product)) {
    throw new RangeError(`${name} exceeds JavaScript's safe integer range.`);
  }
  return product;
}

/** Pure byte-layout calculation shared by allocation planning and self-checks. */
export function getGpuOnlyStorageLayout(
  count: number,
  itemSize: number,
): GpuOnlyStorageLayout {
  const safeCount = checkedPositiveInteger(count, 'count');
  const safeItemSize = checkedPositiveInteger(itemSize, 'itemSize');
  if (safeItemSize < MIN_ITEM_SIZE || safeItemSize > MAX_ITEM_SIZE) {
    throw new RangeError(
      `itemSize must be between ${MIN_ITEM_SIZE} and ${MAX_ITEM_SIZE}; received ${itemSize}.`,
    );
  }

  // array<vec3<T>> has a 16-byte stride in WGSL. Three applies the same 3->4
  // conversion when createStorageAttribute() first sees a storage attribute.
  const gpuItemSize = safeItemSize === 3 ? 4 : safeItemSize;
  const logicalElementCount = checkedProduct(
    safeCount,
    safeItemSize,
    'logical element count',
  );
  const gpuElementCount = checkedProduct(
    safeCount,
    gpuItemSize,
    'GPU element count',
  );
  const logicalByteLength = checkedProduct(
    logicalElementCount,
    BYTES_PER_SCALAR,
    'logical byte length',
  );
  const gpuByteLength = checkedProduct(
    gpuElementCount,
    BYTES_PER_SCALAR,
    'GPU byte length',
  );

  return Object.freeze({
    count: safeCount,
    itemSize: safeItemSize,
    gpuItemSize,
    logicalElementCount,
    gpuElementCount,
    logicalByteLength,
    gpuByteLength,
    cpuPlaceholderByteLength: gpuItemSize * BYTES_PER_SCALAR,
  });
}

function assertFitsDeviceLimits(
  layout: GpuOnlyStorageLayout,
  limits: StorageLimitsLike,
  label: string,
): void {
  const maxBindingSize = limits.maxStorageBufferBindingSize;
  const maxBufferSize = limits.maxBufferSize;
  if (!Number.isSafeInteger(maxBindingSize) || maxBindingSize < 1) {
    throw new Error('GPUDevice.limits.maxStorageBufferBindingSize is unavailable or invalid.');
  }
  if (!Number.isSafeInteger(maxBufferSize) || maxBufferSize < 1) {
    throw new Error('GPUDevice.limits.maxBufferSize is unavailable or invalid.');
  }
  if (layout.gpuByteLength > maxBindingSize) {
    throw new RangeError(
      `${label} requires ${layout.gpuByteLength} bytes, exceeding `
      + `maxStorageBufferBindingSize=${maxBindingSize}.`,
    );
  }
  if (layout.gpuByteLength > maxBufferSize) {
    throw new RangeError(
      `${label} requires ${layout.gpuByteLength} bytes, exceeding `
      + `maxBufferSize=${maxBufferSize}.`,
    );
  }
}

function requireStorageBackend(renderer: GpuOnlyStorageRenderer): StorageBackend {
  const backend = renderer?.backend as Partial<StorageBackend> | null | undefined;
  if (
    backend === null
    || backend === undefined
    || typeof backend.createStorageAttribute !== 'function'
    || typeof backend.get !== 'function'
  ) {
    throw new Error(
      'A materialized Three WebGPU renderer backend with '
      + 'createStorageAttribute() and get() is required.',
    );
  }
  return backend as StorageBackend;
}

function requireBackendRecord(value: unknown): StorageBackendRecord {
  if (typeof value !== 'object' || value === null) {
    throw new Error('Three WebGPU backend returned an invalid storage-attribute record.');
  }
  return value as StorageBackendRecord;
}

function isGpuBuffer(value: unknown): value is GPUBuffer {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as Partial<GPUBuffer>;
  return Number.isSafeInteger(candidate.size)
    && Number.isSafeInteger(candidate.usage)
    && typeof candidate.destroy === 'function';
}

function getArrayConstructor(
  scalarType: GpuOnlyStorageScalarType,
): GpuOnlyStorageArrayConstructor {
  if (scalarType === 'float32') return Float32Array;
  if (scalarType === 'uint32') return Uint32Array;
  throw new RangeError(`Unsupported GPU-only storage scalar type: ${String(scalarType)}.`);
}

function makeReadbackView<TArray extends GpuOnlyStorageArray>(
  arrayConstructor: GpuOnlyStorageArrayConstructor,
  data: ArrayBuffer,
  expectedByteLength: number,
): TArray {
  if (data.byteLength !== expectedByteLength) {
    throw new RangeError(
      `Readback byte length ${data.byteLength} does not match GPU buffer size `
      + `${expectedByteLength}.`,
    );
  }
  return new arrayConstructor(data) as TArray;
}

function resolveUsage(requestedUsage: GPUBufferUsageFlags | undefined): GPUBufferUsageFlags {
  if (typeof GPUBufferUsage === 'undefined') {
    throw new Error('WebGPU GPUBufferUsage constants are unavailable in this environment.');
  }
  const defaultUsage = GPUBufferUsage.STORAGE
    | GPUBufferUsage.VERTEX
    | GPUBufferUsage.COPY_SRC
    | GPUBufferUsage.COPY_DST;
  const usage = requestedUsage ?? defaultUsage;
  if (!Number.isSafeInteger(usage) || usage <= 0) {
    throw new RangeError(`GPU buffer usage must be a positive integer bit mask; received ${usage}.`);
  }
  if ((usage & GPUBufferUsage.STORAGE) === 0) {
    throw new Error('GPU-only StorageBufferAttribute usage must include GPUBufferUsage.STORAGE.');
  }
  if ((usage & GPUBufferUsage.COPY_SRC) === 0) {
    throw new Error(
      'GPU-only StorageBufferAttribute usage must include GPUBufferUsage.COPY_SRC '
      + 'for Three readback support.',
    );
  }
  if ((usage & (GPUBufferUsage.MAP_READ | GPUBufferUsage.MAP_WRITE)) !== 0) {
    throw new Error('Mapped buffer usages cannot be combined with storage usage.');
  }
  return usage;
}

export function createGpuOnlyStorageAttribute(
  renderer: GpuOnlyStorageRenderer,
  device: GPUDevice,
  options: GpuOnlyStorageOptions & { readonly scalarType: 'float32' },
): GpuOnlyStorageAllocation<Float32Array>;
export function createGpuOnlyStorageAttribute(
  renderer: GpuOnlyStorageRenderer,
  device: GPUDevice,
  options: GpuOnlyStorageOptions & { readonly scalarType: 'uint32' },
): GpuOnlyStorageAllocation<Uint32Array>;
export function createGpuOnlyStorageAttribute(
  renderer: GpuOnlyStorageRenderer,
  device: GPUDevice,
  options: GpuOnlyStorageOptions,
): GpuOnlyStorageAllocation;
/**
 * Creates one real GPU-sized allocation while leaving only a one-item typed
 * array on the CPU. Materialization and injection happen synchronously and
 * must run before the attribute is used to build any TSL binding.
 */
export function createGpuOnlyStorageAttribute(
  renderer: GpuOnlyStorageRenderer,
  device: GPUDevice,
  options: GpuOnlyStorageOptions,
): GpuOnlyStorageAllocation {
  if (
    typeof device !== 'object'
    || device === null
    || typeof device.createBuffer !== 'function'
    || typeof device.limits !== 'object'
  ) {
    throw new Error('A live GPUDevice is required to create GPU-only storage.');
  }
  const layout = getGpuOnlyStorageLayout(options.count, options.itemSize);
  const label = options.label?.trim() || (
    `GPU-only ${options.scalarType} storage ${layout.count}x${layout.itemSize}`
  );
  const usage = resolveUsage(options.usage);
  assertFitsDeviceLimits(layout, device.limits, label);

  const backend = requireStorageBackend(renderer);
  const arrayConstructor = getArrayConstructor(options.scalarType);
  // Materialize exactly one item. This is deliberately done before replacing
  // attribute.count with the logical count so Three's vec3 padding path can
  // never allocate a full-size CPU array.
  const placeholder = new arrayConstructor(layout.itemSize);
  const attribute = new StorageBufferAttribute(placeholder, layout.itemSize);
  attribute.name = label;
  backend.createStorageAttribute(attribute);

  const backendRecord = requireBackendRecord(backend.get(attribute));
  const materializedBuffer = backendRecord.buffer;
  if (!isGpuBuffer(materializedBuffer)) {
    throw new Error(
      `${label}: backend materialization did not produce a WebGPU GPUBuffer.`,
    );
  }
  if (!(attribute.array instanceof arrayConstructor)) {
    materializedBuffer.destroy();
    throw new Error(`${label}: backend changed the placeholder scalar array type.`);
  }
  if (
    attribute.itemSize !== layout.gpuItemSize
    || attribute.array.byteLength !== layout.cpuPlaceholderByteLength
  ) {
    materializedBuffer.destroy();
    throw new Error(
      `${label}: unexpected backend storage layout `
      + `(itemSize=${attribute.itemSize}, CPU bytes=${attribute.array.byteLength}).`,
    );
  }

  let gpuBuffer: GPUBuffer | null = null;
  try {
    gpuBuffer = device.createBuffer({
      label,
      size: layout.gpuByteLength,
      usage,
    });
    if (gpuBuffer.size !== layout.gpuByteLength) {
      throw new Error(
        `${label}: created GPUBuffer size ${gpuBuffer.size} does not match `
        + `${layout.gpuByteLength}.`,
      );
    }
    if ((gpuBuffer.usage & usage) !== usage) {
      throw new Error(`${label}: created GPUBuffer does not retain the requested usage flags.`);
    }

    backendRecord.buffer = gpuBuffer;
    if (backend.get(attribute) !== backendRecord || backendRecord.buffer !== gpuBuffer) {
      throw new Error(`${label}: backend GPUBuffer injection did not persist.`);
    }

    // BufferAttribute.count is runtime-writable but currently marked readonly
    // by @types/three. TSL consumers created after this point see the true count.
    (attribute as unknown as { count: number }).count = layout.count;
    if (attribute.count !== layout.count) {
      throw new Error(`${label}: failed to publish the logical attribute count.`);
    }
  } catch (error) {
    if (backendRecord.buffer === gpuBuffer) backendRecord.buffer = materializedBuffer;
    gpuBuffer?.destroy();
    materializedBuffer.destroy();
    throw error;
  }

  // No binding can reference this one-item materialization yet; the real
  // allocation is now the backend record's buffer.
  materializedBuffer.destroy();

  let destroyed = false;
  const allocation: GpuOnlyStorageAllocation = {
    ...layout,
    attribute,
    buffer: gpuBuffer,
    scalarType: options.scalarType,
    usage,
    label,
    readbackArrayConstructor: arrayConstructor,
    get destroyed(): boolean {
      return destroyed;
    },
    createReadbackView(data: ArrayBuffer): GpuOnlyStorageArray {
      return makeReadbackView(arrayConstructor, data, layout.gpuByteLength);
    },
    destroy(): void {
      if (destroyed) return;
      destroyed = true;
      gpuBuffer.destroy();
    },
  };
  return Object.freeze(allocation);
}

/**
 * Small, deterministic, GPU-free contract check. It is intentionally not
 * wired into the browser regression page; callers may invoke it from a unit
 * test or development assertion without allocating WebGPU resources.
 */
export function runGpuOnlyStoragePureSelfCheck(): GpuOnlyStorageSelfCheckResult {
  const failures: string[] = [];
  let checks = 0;
  const check = (name: string, condition: boolean): void => {
    checks++;
    if (!condition) failures.push(name);
  };

  const floats = getGpuOnlyStorageLayout(7, 4);
  check('float32 vec4 GPU bytes', floats.gpuByteLength === 112);
  check('float32 vec4 placeholder bytes', floats.cpuPlaceholderByteLength === 16);

  const uints = getGpuOnlyStorageLayout(11, 1);
  check('uint32 scalar GPU bytes', uints.gpuByteLength === 44);
  check('uint32 scalar placeholder bytes', uints.cpuPlaceholderByteLength === 4);

  const vec3 = getGpuOnlyStorageLayout(5, 3);
  check('vec3 physical stride', vec3.gpuItemSize === 4);
  check('vec3 padded GPU bytes', vec3.gpuByteLength === 80);
  check('vec3 logical bytes', vec3.logicalByteLength === 60);

  const floatView = makeReadbackView<Float32Array>(Float32Array, new ArrayBuffer(16), 16);
  const uintView = makeReadbackView<Uint32Array>(Uint32Array, new ArrayBuffer(16), 16);
  check('float32 readback constructor', floatView instanceof Float32Array);
  check('uint32 readback constructor', uintView instanceof Uint32Array);

  let rejectedBadScalarType = false;
  try {
    getArrayConstructor('float64' as GpuOnlyStorageScalarType);
  } catch {
    rejectedBadScalarType = true;
  }
  check('reject unsupported scalar type', rejectedBadScalarType);

  let rejectedBadItemSize = false;
  try {
    getGpuOnlyStorageLayout(1, 5);
  } catch {
    rejectedBadItemSize = true;
  }
  check('reject unsupported itemSize', rejectedBadItemSize);

  let rejectedBindingOverflow = false;
  try {
    assertFitsDeviceLimits(
      getGpuOnlyStorageLayout(17, 1),
      { maxStorageBufferBindingSize: 64, maxBufferSize: 128 },
      'self-check',
    );
  } catch {
    rejectedBindingOverflow = true;
  }
  check('reject storage binding overflow', rejectedBindingOverflow);

  return Object.freeze({
    pass: failures.length === 0,
    checks,
    failures: Object.freeze(failures),
  });
}
